import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Share2, PenTool, Palette, FileText, Video, TrendingUp, Search, Users,
  Star, Tv, Globe, Megaphone, ArrowRight, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SERVICES, waLink } from "@/data/site";
import { SectionHeading } from "@/components/pp/Shared";
import { track } from "@/lib/api";
import { scrollTo } from "@/components/pp/Navbar";

const ICONS = {
  "social-media": Share2, "graphic-design": PenTool, "branding": Palette, "content": FileText,
  "video": Video, "performance": TrendingUp, "seo": Search, "influencer": Users,
  "celebrity": Star, "tv-ott": Tv, "web": Globe, "campaign": Megaphone,
};

function Node({ svc, angle, radius, onClick }) {
  const Icon = ICONS[svc.id] || Megaphone;
  const x = Math.cos((angle * Math.PI) / 180) * radius;
  const y = Math.sin((angle * Math.PI) / 180) * radius;
  return (
    <button
      onClick={onClick}
      data-testid={`service-node-${svc.id}`}
      className="absolute left-1/2 top-1/2 group"
      style={{ transform: `translate(-50%,-50%) translate(${x}px, ${y}px)` }}
      aria-label={svc.title}
    >
      <span className="grid place-items-center h-16 w-16 md:h-20 md:w-20 rounded-2xl pp-glass transition-transform duration-300 group-hover:scale-110"
        style={{ boxShadow: `0 0 0 1px ${svc.color}30` }}>
        <Icon size={26} style={{ color: svc.color }} />
      </span>
      <span className="absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap text-[10px] uppercase tracking-widest text-slate-400 group-hover:text-white transition-colors">
        {svc.title.split(" ")[0]}
      </span>
    </button>
  );
}

export default function ServicesUniverse() {
  const [active, setActive] = useState(null);
  const [paused, setPaused] = useState(false);
  const open = (svc) => { setActive(svc); track("service_open", { service: svc.id }); };
  const inner = SERVICES.slice(0, 6);
  const outer = SERVICES.slice(6, 12);
  const play = paused ? "paused" : "running";

  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#050507] overflow-hidden" data-testid="services">
      <div className="pp-container">
        <SectionHeading overline="Services" title={<>Everything Your Brand Needs.<br />Under One Creative Roof.</>}
          sub="Click any node to explore the full scope. Twelve creative disciplines, one coordinated team." testid="services-heading" />

        {/* Orbital — desktop */}
        <div className="hidden lg:block relative mx-auto" style={{ height: 640, maxWidth: 640 }}
          onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/5" style={{ width: 300, height: 300 }} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/5" style={{ width: 560, height: 560 }} />

          {/* center emblem */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[2] grid place-items-center pointer-events-none">
            <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 4, repeat: Infinity }}
              className="h-40 w-40 rounded-full pp-glass grid place-items-center pp-glow-orange">
              <img src="/brand/papad.png" alt="PixelPapad" className="h-24 w-24 object-contain rounded-full" style={{ filter: "drop-shadow(0 0 20px rgba(245,158,11,0.5))" }} />
            </motion.div>
          </div>

          <div className="absolute inset-0" style={{ animation: "pp-spin 60s linear infinite", animationPlayState: play }}>
            {inner.map((s, i) => (
              <div key={s.id} style={{ animation: "pp-spin-rev 60s linear infinite", animationPlayState: play }} className="absolute inset-0">
                <Node svc={s} angle={(360 / inner.length) * i - 90} radius={150} onClick={() => open(s)} />
              </div>
            ))}
          </div>
          <div className="absolute inset-0" style={{ animation: "pp-spin-rev 90s linear infinite", animationPlayState: play }}>
            {outer.map((s, i) => (
              <div key={s.id} style={{ animation: "pp-spin 90s linear infinite", animationPlayState: play }} className="absolute inset-0">
                <Node svc={s} angle={(360 / outer.length) * i - 60} radius={280} onClick={() => open(s)} />
              </div>
            ))}
          </div>
        </div>

        {/* Grid — mobile/tablet */}
        <div className="lg:hidden grid grid-cols-2 sm:grid-cols-3 gap-4">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.id];
            return (
              <button key={s.id} onClick={() => open(s)} data-testid={`service-card-${s.id}`}
                className="pp-card rounded-xl p-5 text-left flex flex-col gap-3">
                <Icon size={24} style={{ color: s.color }} />
                <span className="font-display uppercase text-sm leading-tight text-white">{s.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-[#0a0a0e] border-white/10 text-white p-0 overflow-hidden" data-testid="service-modal">
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.title}</DialogTitle>
              <DialogDescription className="sr-only">{active.short}</DialogDescription>
              <div className="p-8 pb-6" style={{ background: `radial-gradient(circle at 0% 0%, ${active.color}22, transparent 60%)` }}>
                <span className="pp-overline" style={{ color: active.color }}>Service</span>
                <h3 className="font-display uppercase text-3xl md:text-4xl mt-3 tracking-tight">{active.title}</h3>
                <p className="mt-2 text-slate-400">{active.short}</p>
                {active.price && <span className="inline-block mt-4 px-3 py-1 rounded-full text-xs font-semibold bg-white/10">{active.price}</span>}
              </div>
              <div className="px-8 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-h-[40vh] overflow-y-auto">
                {active.items.map((it) => (
                  <div key={it} className="flex items-center gap-2 text-sm text-slate-300 py-1">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: active.color }} /> {it}
                  </div>
                ))}
              </div>
              {active.note && <p className="px-8 text-xs text-[#f59e0b] mb-2">{active.note}</p>}
              <div className="p-8 pt-4 flex flex-wrap gap-3 border-t border-white/5">
                <a href={waLink(`Hi PixelPapad, I'm interested in ${active.title}.`)} target="_blank" rel="noopener noreferrer"
                  onClick={() => { track("whatsapp_click", { location: "service_modal", service: active.id }); track("service_cta", { service: active.id }); }}
                  data-testid="service-modal-cta"
                  className="inline-flex items-center gap-2 bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-white transition-colors">
                  {active.cta} <ArrowRight size={16} />
                </a>
                <button onClick={() => { setActive(null); scrollTo("contact"); }}
                  className="inline-flex items-center gap-2 border border-white/15 text-white font-semibold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-white/5 transition-colors">
                  Enquire
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
